/**
 * Norce Checkout Types
 * TypeScript interfaces for the Norce Checkout with multiple payment adapters:
 * - Adyen: Card payments, Apple Pay, Google Pay via Adyen Drop-in
 * - Klarna: Klarna Checkout widget (embedded HTML snippet)
 * - Walley: Walley Checkout widget (embedded HTML snippet)
 * - NonPsp: Invoice payments, free orders (no external PSP)
 */

// Customer address types (for B2C address updates via Norce Checkout)
export type NorceCheckoutAddress = {
  type?: 'person' | 'organization';
  givenName?: string;
  familyName?: string;
  email?: string;
  phone?: string;
  streetAddress?: string;
  streetAddress2?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  region?: string;
  careOf?: string;
  organizationName?: string;
  organizationIdentificationNumber?: string;
}

export type NorceCheckoutCustomer = {
  type: 'person' | 'organization';
  billing?: NorceCheckoutAddress;
  shipping?: NorceCheckoutAddress;
}

// Request type for updating billing/shipping address
export type UpdateAddressRequest = {
  orderId: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  organizationNumber?: string;
  reference?: string;
  careOf?: string;
  deliveryAddress?: {
    line1?: string;
    line2?: string;
    zip?: string;
    city?: string;
    country?: string;
    region?: string;
  };
}

// Initialize request/response types
export type InitializeRequest = {
  basketUuid: string;
}

export type PaymentAdapter = 'adyen' | 'klarna' | 'walley' | 'nonpsp';

// Norce order lifecycle state. Customer/address updates are only allowed while
// the order is 'checkout' or 'processing'.
export type OrderState = 'checkout' | 'processing' | 'accepted' | 'completed' | 'declined' | 'removed';

// Order states in which /customer address updates are permitted
export const EDITABLE_ORDER_STATES: OrderState[] = ['checkout', 'processing'];

export type InitializeResponse = {
  orderId: string;
  orderState?: OrderState; // Norce order lifecycle state
  paymentId?: string; // May be at top level or nested in payment data
  paymentAdapter: PaymentAdapter;
  payment?: PaymentData; // Unified payment data (type determined by paymentAdapter)
  customer?: NorceCheckoutCustomer; // Customer billing/shipping data for pre-filling forms
}

// Non-PSP payment types (for free orders, samples, etc.)
export type NonPspPaymentData = {
  paymentId: string; // Payment ID
  paymentMethods: NonPspPaymentMethod[];
  selectedPaymentMethod: string | null;
}

export type NonPspPaymentMethod = {
  identifier: string; // Payment method identifier (e.g., "INVOICE")
  name?: string | null;
  description?: string | null;
}

// Klarna payment types
export type KlarnaPaymentData = {
  paymentId: string; // Payment ID
  status?: string; // Klarna checkout status (e.g., 'checkout_incomplete')
  htmlSnippet?: string; // HTML to embed in the page
}

// Klarna confirmation response (for success page)
export type KlarnaConfirmationResponse = {
  payment: KlarnaPaymentData;
}

// Walley payment types
export type WalleyPaymentData = {
  paymentId: string; // Payment ID
  publicToken?: string | null; // Token used to render the checkout iframe
  htmlSnippet?: string | null; // HTML to embed in the page
}

// Walley confirmation response (for success page)
export type WalleyConfirmationResponse = {
  payment: WalleyPaymentData;
}

// Unified payment data type (union of all adapter-specific payment data)
export type PaymentData = AdyenPaymentData | KlarnaPaymentData | WalleyPaymentData | NonPspPaymentData;

// Available adapters response (GET /norce-checkout/adapters)
export type AvailableAdaptersResponse = {
  default: PaymentAdapter;
  available: PaymentAdapter[];
  hasInvoice: boolean;
}

// Switch adapter request/response (PUT /norce-checkout/adapter/{basketUuid})
export type SwitchAdapterRequest = {
  adapter: PaymentAdapter;
}

export type SwitchAdapterResponse = {
  switched: boolean;
  orderId: string;
  paymentAdapter: PaymentAdapter;
  paymentId: string;
  payment?: PaymentData; // Unified payment data (type determined by paymentAdapter)
  customer?: NorceCheckoutCustomer;
}

// Adyen transaction request/response types
export type AdyenTransactionRequest = {
  orderId: string;
  paymentId: string;
  transactionData: AdyenTransactionData;
}

export type AdyenTransactionResponse = {
  resultCode: AdyenResultCode;
  action?: AdyenAction;
  order?: NorceOrderResult;
  refusalReason?: string;
  refusalReasonCode?: string;
}

// Adyen details request/response types (for 3DS, Swish, etc.)
export type AdyenDetailsRequest = {
  orderId: string;
  paymentId: string;
  details: Record<string, unknown>;
  paymentData?: string; // Required for redirect payments (Swish, iDEAL, etc.)
}

export type AdyenDetailsResponse = {
  resultCode: AdyenResultCode;
  action?: AdyenAction;
  order?: NorceOrderResult;
}

// Adyen refresh response type
export type AdyenRefreshResponse = {
  orderId: string;
  paymentId: string;
  payment: AdyenPaymentData;
}

// Payment status response type (for /status/{basketUuid} endpoint)
export type PaymentStatusResponse = {
  orderId: string;
  orderState?: OrderState; // Norce order lifecycle state
  paymentAdapter: PaymentAdapter;
  payment?: PaymentData; // Unified payment data (type determined by paymentAdapter)
  paymentError?: string; // Error message if payment initialization failed
  customer?: NorceCheckoutCustomer; // Customer billing/shipping data
}

// Adyen specific types
export type AdyenPaymentData = {
  paymentId: string; // Payment ID
  paymentMethodsResponse: AdyenPaymentMethodsResponse;
  clientKey: string;
  environment: 'test' | 'live';
  amount: AdyenAmount;
  countryCode?: string;
  locale: string;
  shopperReference?: string;
  paymentMethodsConfiguration?: Record<string, unknown> | null;
}

export type AdyenPaymentMethodsResponse = {
  paymentMethods: AdyenPaymentMethod[];
  storedPaymentMethods?: AdyenStoredPaymentMethod[];
}

export type AdyenPaymentMethod = {
  type: string;
  name: string;
  brands?: string[];
  configuration?: Record<string, unknown>;
}

export type AdyenStoredPaymentMethod = {
  id: string;
  type: string;
  name: string;
  brand?: string;
  lastFour?: string;
  expiryMonth?: string;
  expiryYear?: string;
}

export type AdyenAmount = {
  value: number;
  currency: string;
}

export type AdyenTransactionData = {
  paymentMethod: Record<string, unknown>;
  browserInfo?: Record<string, unknown>;
  billingAddress?: AdyenAddress;
  deliveryAddress?: AdyenAddress;
}

export type AdyenAddress = {
  street: string;
  houseNumberOrName: string;
  city: string;
  postalCode: string;
  country: string;
  stateOrProvince?: string;
}

export type AdyenResultCode =
  | 'Authorised'
  | 'Refused'
  | 'Pending'
  | 'Cancelled'
  | 'Error'
  | 'RedirectShopper'
  | 'IdentifyShopper'
  | 'ChallengeShopper'
  | 'PresentToShopper';

// Base Adyen action properties
export type AdyenActionBase = {
  type: string;
  paymentMethodType: string;
  url?: string;
  method?: string;
  data?: Record<string, string>;
  paymentData?: string;
  token?: string;
  subtype?: string;
}

// Action can be direct or wrapped in actualInstance (backend compatibility)
export type AdyenAction = AdyenActionBase & {
  actualInstance?: AdyenActionBase;
}

// Norce order result
export type NorceOrderResult = {
  orderId: string;
  orderNo: string;
  basketId: string;
}

// Non-PSP response types
export type NonPspSelectMethodResponse = {
  payment: NonPspPaymentData;
}

export type NonPspCompleteResponse = {
  success: boolean;
  order?: NorceOrderResult;
}

// State type for the composable
export type NorceCheckoutState = {
  orderId: string | null;
  orderState: OrderState | null;
  paymentId: string | null;
  paymentAdapter: PaymentAdapter | null;
  payment: PaymentData | null;
  customer: NorceCheckoutCustomer | null;
  availableAdapters: PaymentAdapter[];
  defaultAdapter: PaymentAdapter | null;
  hasInvoice: boolean;
  isInitializing: boolean;
  isProcessing: boolean;
  isSwitchingAdapter: boolean;
  error: string | null;
  resultCode: AdyenResultCode | null;
}
